import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { IconChevronDown, IconX } from "@tabler/icons";
import { useTheme } from "styled-components";

import { TextInput } from "@sso/components/Form/TextInput";
import { useClickAwayListener } from "@sso/hooks/useClickAwayListener";
import { Divider } from "@sso/ui/Divider";
import { ColumnContainer, ColumnContainerProps, Flex, RowContainer, RowContainerProps, Wrapper } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";
import { MenuContainer, MenuItem } from "@sso/ui/Menu";
import { Typography } from "@sso/ui/Typography";

export type SelectDataType = { key: string | number; value: string | number };
export const nonSelectedItemValue: SelectDataType = { key: -1, value: "Select ..." };
const defaultValueForEmptyData = (emptyLable?: string): SelectDataType => ({ key: -1, value: emptyLable || "Empty" });

export interface SelectProps extends Omit<ColumnContainerProps, "maxHeight"> {
  onChange?: (data: SelectDataType[]) => void;
  label?: string;
  items: SelectDataType[] | string[];
  error?: boolean;
  name?: string;
  disabled?: boolean;
  maxHeight?: number;
  value?: SelectDataType[];
  searchable?: boolean;
  clearable?: boolean;
  emptyLable?: string;
  heightOffset?: number;
  fieldProps?: RowContainerProps;
  openUpward?: boolean;
}

export const MultiSelect = ({
  onChange,
  label,
  items,
  error,
  disabled,
  value,
  emptyLable,
  fieldProps,
  heightOffset = 0,
  maxHeight = 250,
  clearable = true,
  searchable = false,
  openUpward = false,
  ...containerProps
}: SelectProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const [isCloseToBottom, setIsCloseToBottom] = useState<boolean>(openUpward);
  const [searchItem, setSearchItem] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<SelectDataType[]>([]);

  const selectContainerRef = useRef<HTMLDivElement>(null!);

  const closeMenu = useCallback(() => setOpen(false), []);
  useClickAwayListener(selectContainerRef, closeMenu);

  const calculatedItems = useMemo(() => {
    if (!items.length) {
      return [];
    }
    const isItemsArrayOfString = typeof items[0] === "string";
    return isItemsArrayOfString
      ? (items as string[]).map((value, key) => ({ key, value } as SelectDataType))
      : (items as SelectDataType[]);
  }, [items]);

  useEffect(() => {
    if (!items.length) {
      return;
    }
    if (value) {
      return setSelectedItems(value);
    }
    // setSelectedItems([nonSelectedItemValue]);
  }, [items, value]);

  useEffect(() => {
    if (!selectContainerRef.current || openUpward) {
      return;
    }
    const remainingSpaceToBottom =
      window.innerHeight -
      (heightOffset + selectContainerRef.current.offsetTop + selectContainerRef.current.offsetHeight);
    if (remainingSpaceToBottom < maxHeight + 10) {
      setIsCloseToBottom(true);
    }
  }, [heightOffset, maxHeight, openUpward]);

  const searchedItemsList = calculatedItems.filter(item => {
    return item.value.toString().toLowerCase().includes(searchItem);
  });

  const handleDeleteSelectedItem = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    if (!items.length) {
      return;
    }
    setSelectedItems([]);
    onChange && onChange([]);
  };

  const handleOpenItems = () => {
    !disabled && calculatedItems.length > 0 && setOpen(open => !open);
  };

  const handleOnChange = (item: SelectDataType) => {
    let newSelectedItems = [...selectedItems];
    if (newSelectedItems.filter(x => x.key === item.key).length > 0) {
      newSelectedItems = newSelectedItems.filter(x => x.key != item.key);
    } else {
      newSelectedItems = [...newSelectedItems, item];
    }

    onChange && onChange(newSelectedItems);
    setSelectedItems(newSelectedItems);
    // handleOpenItems();
  };

  const handleSearchItem = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchItem(e.target.value.toLowerCase());
  };

  const getColor = (isLabel?: boolean) => {
    if (disabled) {
      return theme.colors.action.disabled;
    } else if (error) {
      return theme.colors.error.main;
    } else if (isLabel) {
      return theme.colors.grey[600];
    } else {
      //const isDefaultValue = selectedItem.value === nonSelectedItemValue.value || selectedItem.value === emptyLable;
      return theme.colors.text.primary;
    }
  };

  return (
    <ColumnContainer
      ref={selectContainerRef}
      position="relative"
      margin="0 0 1rem 0"
      padding="0 0.2rem"
      align="flex-start"
      justify="flex-start"
      width="100%"
      {...containerProps}
    >
      {label && (
        <Wrapper position="absolute" left="1rem" top="-6px" padding="0 0.25rem" bgColor="white">
          <Typography
            color={open ? theme.colors.primary.main : getColor(true)}
            size="0.75rem"
            textTransform="capitalize"
          >
            {label}
          </Typography>
        </Wrapper>
      )}
      <RowContainer
        onClick={handleOpenItems}
        padding="0.65rem 0.75rem"
        width="100%"
        justify="space-between"
        border={`1px solid ${open ? theme.colors.primary.main : theme.colors.grey[500]}`}
        rounded
        {...fieldProps}
      >
        <Typography color={getColor()} overflowEllipsis>
          {selectedItems.length > 0 &&
            selectedItems.map((item, index) => item.value.toString() + (index < selectedItems.length - 1 ? ", " : ""))}
          {selectedItems.length == 0 && <>Select ...</>}
        </Typography>

        <Flex margin="0 0 0 0.25rem">
          {clearable && (
            <>
              <IconButton tabIndex={-1} disabled={disabled} margin="0" padding="0" onClick={handleDeleteSelectedItem}>
                <IconX size={20} />
              </IconButton>
              <Divider direction="v" width="2px" height="20px" margin="0 0.25rem" />
            </>
          )}
          <IconButton tabIndex={-1} disabled={disabled} margin="0" padding="0">
            <IconChevronDown />
          </IconButton>
        </Flex>
      </RowContainer>
      {open && (
        <MenuContainer
          maxHeight={`${maxHeight}px`}
          left="5px"
          right="5px"
          top={!isCloseToBottom ? "110%" : "auto"}
          bottom={isCloseToBottom ? "0" : undefined}
        >
          {searchable && <TextInput placeholder="Search..." onChange={handleSearchItem} value={searchItem} autoFocus />}
          {searchedItemsList.map(item => (
            <MenuItem key={item.key} title={item.value.toString()} onClick={() => handleOnChange(item)}>
              <input type="checkbox" checked={selectedItems.filter(x => x.key === item.key).length > 0} /> {item.value}
            </MenuItem>
          ))}

          {calculatedItems.length > 0 && !searchedItemsList.length && (
            <Typography textAlign="center" display="block" color={theme.colors.grey[500]}>
              Not Found
            </Typography>
          )}
        </MenuContainer>
      )}
    </ColumnContainer>
  );
};
